/**
 * Stufenlos zwischen Hell und Dunkel.
 *
 * Die Helligkeit ist eine Zahl von 0 (ganz hell) bis 1 (ganz dunkel).
 * Dazwischen werden die Farben beider Fassungen gemischt. Gespeichert wird
 * sie in derselben Einstellung wie das Theme, als "tone:0.42" – so braucht
 * es kein neues Feld und aeltere Werte ("paper", "dark", "auto") gelten
 * weiter.
 */

export const TONE_PREFIX = 'tone:';

/** Theme-Wert fuer eine Helligkeit; die Enden sind die festen Themes. */
export function toneThemeId(tone: number): string {
  const t = clampTone(tone);
  if (t <= 0) return 'paper';
  if (t >= 1) return 'dark';
  return TONE_PREFIX + t.toFixed(2);
}

/** Helligkeit aus einem Theme-Wert; null bei "auto" oder unbekannt. */
export function parseTone(id: string): number | null {
  if (id === 'paper') return 0;
  if (id === 'dark') return 1;
  if (!id.startsWith(TONE_PREFIX)) return null;
  const n = Number(id.slice(TONE_PREFIX.length));
  return Number.isFinite(n) ? clampTone(n) : null;
}

export function clampTone(t: number): number {
  return Math.min(1, Math.max(0, Math.round(t * 100) / 100));
}

type Rgba = [number, number, number, number];

/** Liest #rgb, #rrggbb, rgb(), rgba() und transparent. Sonst null. */
export function parseColor(value: string): Rgba | null {
  const v = value.trim().toLowerCase();
  if (v === 'transparent') return [0, 0, 0, 0];
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(v);
  if (hex) {
    let s = hex[1] as string;
    if (s.length === 3) s = s.replace(/./g, (c) => c + c);
    const n = parseInt(s, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 1];
  }
  const fn = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+))?\s*\)$/.exec(v);
  if (fn) return [Number(fn[1]), Number(fn[2]), Number(fn[3]), fn[4] === undefined ? 1 : Number(fn[4])];
  return null;
}

function formatColor([r, g, b, a]: Rgba): string {
  const c = (n: number) => Math.round(n);
  if (a >= 1) return `rgb(${c(r)}, ${c(g)}, ${c(b)})`;
  return `rgba(${c(r)}, ${c(g)}, ${c(b)}, ${Number(a.toFixed(3))})`;
}

/**
 * Mischt zwei Farben. t = 0 ergibt a, t = 1 ergibt b. Laesst sich eine
 * Farbe nicht lesen (z. B. var(...)), gilt die naehere der beiden.
 */
export function mixColor(a: string, b: string, t: number): string {
  if (t <= 0) return a;
  if (t >= 1) return b;
  const ca = parseColor(a);
  const cb = parseColor(b);
  if (!ca || !cb) return t < 0.5 ? a : b;
  // Durchsichtig mischt ohne Grauschleier: nur die Deckkraft wandert.
  const from: Rgba = ca[3] === 0 ? [cb[0], cb[1], cb[2], 0] : ca;
  const to: Rgba = cb[3] === 0 ? [ca[0], ca[1], ca[2], 0] : cb;
  return formatColor(from.map((v, i) => v + ((to[i] as number) - v) * t) as Rgba);
}


/**
 * Verlauf der Flaechen ueber den Regler: an den Enden feine Stufen, durch
 * das mittlere Grau zuegig hindurch (dort ist Text am schlechtesten lesbar).
 */
export function shapeTone(t: number): number {
  if (t <= 0 || t >= 1) return t <= 0 ? 0 : 1;
  return t - (0.7 * Math.sin(2 * Math.PI * t)) / (2 * Math.PI);
}

/**
 * Ab hier (auf der geformten Skala) ist der Grund so dunkel, dass helle
 * Schrift besser lesbar ist als dunkle. Schriftfarben mischen nicht mit,
 * sie wechseln hier die Seite – gemischt waeren Schrift und Grund in der
 * Mitte beide grau.
 */
export const TEXT_FLIP = 0.62;

export function isDarkSide(t: number): boolean {
  return shapeTone(t) >= TEXT_FLIP;
}
